import React, { useEffect, useRef, useState } from "react";
import { Tree } from "antd";
import type { TreeDataNode as AntdTreeDataNode } from "antd";
import { openNotificationWithIcon } from "helpers/OpenNotificationWithIcon";
import {
  getCategoryFilter,
  getCategoryTreeById,
  getCategoryTreeWithChildrenFilter,
  getCategoryTreeByUrlFilter,
  GetCategoryTreeWithTopCategoryByUrlAsync
} from "apps/CustomerPanel/Ad/Services";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { handleSetAdvertListFilter } from "../ClientStates";
import { RootState } from "store/Reducers";
import GeneralLoadingSpinner from "apps/Common/GeneralLoadingSpinner";
// Extend TreeDataNode to include custom properties
interface TreeDataNode extends AntdTreeDataNode {
  url?: string;
  originalId?: string | number;
  description?: string;
  type?: number;
  displayUrl?: string;
  filterUrl?: string;
  children?: TreeDataNode[];
}


const TreeCategories: React.FC<{ wrapperClasName?: string }> = ({
  wrapperClasName,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [treeData, setTreeData] = useState<TreeDataNode[]>([]);
  const { filter } = useSelector((state: RootState) => state.realState);
  const dispatch = useDispatch();
  const treeRef = useRef<HTMLDivElement>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const params = useParams();
  const url = params["categoryUrl"];

  // URL'den expanded keys'i hesaplayan fonksiyon
  const calculateExpandedKeysFromUrl = (nodes: TreeDataNode[], currentUrl: string): React.Key[] => {
    const expandedKeys: React.Key[] = [];
    
    const findAndExpandParents = (nodeList: TreeDataNode[], targetUrl: string): boolean => {
      for (const node of nodeList) {
        const nodeUrl = node.url as string;
        
        // Eğer current URL bu node'un URL'ini içeriyorsa veya eşitse
        if (targetUrl && nodeUrl && (targetUrl.includes(nodeUrl) || nodeUrl === targetUrl)) {
          // Bu node'u expand et
          if (node.children && node.children.length > 0) {
            expandedKeys.push(node.key);
          }
          
          // Alt node'larda da ara
          if (node.children && node.children.length > 0) {
            findAndExpandParents(node.children, targetUrl);
          }
          
          // Eğer tam eşleşme varsa, parent'ları da expand et
          if (nodeUrl === targetUrl) {
            return true;
          }
        }
      }
      return false;
    };
    
    if (currentUrl) {
      findAndExpandParents(nodes, currentUrl);
    }
    
    return expandedKeys;
  };

  // Parent key'i bulan fonksiyon
  const findParentKey = (nodes: TreeDataNode[], targetKey: React.Key, parentKey?: React.Key): React.Key | null => {
    for (const node of nodes) {
      if (node.key === targetKey) {
        return parentKey || null;
      }
      if (node.children && node.children.length > 0) {
        const found = findParentKey(node.children, targetKey, node.key);
        if (found !== null) {
          return found;
        }
      }
    }
    return null;
  };

  // Seçim olayını ele alan fonksiyon
  const onSelect = async (selectedKeys: React.Key[], info: any) => {
    if (filter?.IsSearchFilter) {
      const currentFilter = { ...filter, IsSearchFilter: false };
     
      dispatch(handleSetAdvertListFilter({ filter: currentFilter }));
    }
    
    // Orijinal ID'yi al (eğer varsa)
    const categoryId = info?.node?.originalId || info?.node?.key;
    

    if (!categoryId) return;
    
    // Son eleman kontrolü
    const isLeafNode = !info?.node?.children || info?.node?.children.length === 0;
    
    // Seçilen node'un parent'ını expanded keys'e ekle
    if (isLeafNode) {
    
      const parentKey = findParentKey(treeData, info?.node?.key);
      if (parentKey) {
        // Parent key'i localStorage'a kaydet (sayfa yenilendiğinde korunması için)
        const savedExpandedKeys = JSON.parse(localStorage.getItem('treeExpandedKeys') || '[]');
        if (!savedExpandedKeys.includes(parentKey)) {
          savedExpandedKeys.push(parentKey);
          localStorage.setItem('treeExpandedKeys', JSON.stringify(savedExpandedKeys));
        }
      }
    }
    
    // Mevcut expanded keys'i kaydet
    localStorage.setItem('treeExpandedKeys', JSON.stringify(expandedKeys));
    
    const url = `/ilan/${info?.node?.url}`;
   
    navigate(url);
  };

  // Tree data güncelleme fonksiyonu
  const updateTreeData = (
    list: TreeDataNode[],
    key: React.Key,
    children: TreeDataNode[]
  ): TreeDataNode[] =>
    list.map((node) => {
      if (node.key === key) {
        return { ...node, children };
      }
      if (node.children) {
        return {
          ...node,
          children: updateTreeData(node.children, key, children),
        };
      }
      return node;
    });

  // Kategori listesi alma fonksiyonu
  const getCategoryList = async (
    parentId: string | null,
    isChild: boolean = false
  ) => {
    try {
      const response = await getCategoryFilter(
        parentId ? { ParentCategoryId: parentId, PageSize: -1 } : null
      );

      if (parentId) {
       
        const childrenData = response?.data?.data.map((item: any, index: number) => ({
          title: (
            <span>
              {item.name}
              <span className="!text-xs !text-gray-300">
                ({item.advertCount})
              </span>
            </span>
          ),
          key: `${parentId}-${item.id}-${index}`, // Unique key oluştur
          originalId: item.id, // Orijinal ID'yi sakla
          children: [],
          description: item?.description || "",
          url: item?.url || "",
          type: item.type,
          displayUrl: item?.displayUrl,
        }));

        if (childrenData) {
          setTreeData((prevData) =>
            updateTreeData(prevData, parentId, childrenData)
          );
        }
      } else {
        const data = response?.data?.data?.map((item: any, index: number) => ({
          title: (
            <span>
              {item.name}
              <span className="!text-xs !text-gray-400 !ml-1">
                ({item.advertCount})
              </span>
            </span>
          ),
          key: `root-${item.id}-${index}`, // Unique key oluştur
          originalId: item.id, // Orijinal ID'yi sakla
          children: [],
          description: item?.description || "",
          url: item?.url || "",
          type: item.type,
          displayUrl: item?.displayUrl,
        }));
        setTreeData(data);
        
        // Ana kategoriler yüklendiğinde localStorage'dan expanded keys'i al
        const savedExpandedKeys = JSON.parse(localStorage.getItem('treeExpandedKeys') || '[]');
        setExpandedKeys(savedExpandedKeys);
      }
    } catch (error: any) {
      openNotificationWithIcon("default", error?.message || "Bir hata oluştu");

    }
  };

  // Kategori tree formatı
  const formatCategoryTree = (category: any, parentId?: string) => {
    // const finalUrl =
    //   category.type === 1
    //     ? category.filterUrl || category.url
    //     : category.url;
    
    // Unique key oluştur (parent ID ile combine et)
    const uniqueKey = parentId ? `${parentId}-${category.id}` : `root-${category.id}`;
    
    return {
      title: (
        <span>
          {category.name}
          <span className="!text-xs !text-gray-400">
            ({category.advertCount})
          </span>
        </span>
      ),
      key: uniqueKey,
      originalId: category.id, // Orijinal ID'yi sakla
      description: category?.description,
      url: category?.filterUrl || "",
      filterUrl: category?.filterUrl || "",
      type: category.type,
      displayUrl: category?.displayUrl,
      children: category.subCategories?.map((subCat: any) => formatCategoryTree(subCat, category.id)) || [],
    };
  };

  // Alt kategoriler ile birlikte kategori alma
  const getCategoryWithChildren = async () => {
    setIsLoading(true);
    try {
      const response = await GetCategoryTreeWithTopCategoryByUrlAsync({
        Url: url,
      });
      
      if (!response) {
        const newResponse = await GetCategoryTreeWithTopCategoryByUrlAsync({
          Url: url,
        });
      }

      if (response?.data) {
        const findCategoryByUrl = async (category: any, url: string): Promise<any | null> => {
          if (category.url === url) {
            return category;
          } else if (category.type === 1) {
            if (category.filterUrl === url || category.url === url) {
              return category;
            } else {
              const newResponse1 = await getCategoryTreeByUrlFilter({
                Url: url,
              });
              return newResponse1.data;
            }
          }

          for (const subCategory of category.subCategories || []) {
            const found = await findCategoryByUrl(subCategory, url);
            if (found) return found;
          }

          return null;
        };

        const formattedTreeData = formatCategoryTree(response.data);
    
        const matchedCategory = await findCategoryByUrl(response.data, url || "");
      
        
        // Tree data'yı set et
        setTreeData([formattedTreeData]);
        
        // URL'den expanded keys'i hesapla
        const urlBasedExpandedKeys = calculateExpandedKeysFromUrl([formattedTreeData], url || "");
        
        // LocalStorage'dan saved expanded keys'i al
        const savedExpandedKeys = JSON.parse(localStorage.getItem('treeExpandedKeys') || '[]');
        
        // İkisini birleştir (duplicate'ları kaldır)
        const combinedExpandedKeys = Array.from(new Set([...urlBasedExpandedKeys, ...savedExpandedKeys]));
        
        setExpandedKeys(combinedExpandedKeys);
        
        // Filter'ı güncelle
        const currentFilter = { ...filter };
        currentFilter["CategoryId"] = matchedCategory?.id || null;
        currentFilter["mainCategoryOptionId"] =
          matchedCategory?.type === 0
            ? matchedCategory?.id
            : response?.data?.subCategories[0]?.id;
        currentFilter["categoryUrl"] = matchedCategory?.url || "";
        currentFilter["categoryDescription"] =
          matchedCategory?.description || "";
        currentFilter["categoryDisplayUrl"] = matchedCategory?.displayUrl || "";
        currentFilter["categoryType"] = matchedCategory?.type;

        dispatch(handleSetAdvertListFilter({ filter: currentFilter }));
      }
    } catch (error: any) {
      console.log("somethings wrong during getting category", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Varsayılan expanded keys'i alma
  const getDefaultExpandedKeys = (nodes: TreeDataNode[]): React.Key[] => {
    let keys: React.Key[] = [];
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        keys.push(node.key);
        keys = keys.concat(getDefaultExpandedKeys(node.children));
      }
    });
    return keys;
  };

  // Component mount olduğunda çalışan effect
  useEffect(() => {
    const currentFilter = { ...filter };
    if (url) {
      getCategoryWithChildren();
    } else {
      delete currentFilter["Type"];
      getCategoryList(null);
    }

    dispatch(handleSetAdvertListFilter({ filter: currentFilter }));
  }, [params]);

  // Tree data değiştiğinde çalışan effect
  useEffect(() => {
    // Eğer expanded keys yoksa ve URL yoksa, tüm tree'yi aç
    if (expandedKeys.length === 0 && !url) {
      const defaultKeys = getDefaultExpandedKeys(treeData);
      setExpandedKeys(defaultKeys);
    }
    
    // Container height'ı güncelle
    if (treeRef.current) {
      const currentFilter = { ...filter };
      currentFilter["categoryContainer"] = treeRef.current.clientHeight;
      dispatch(handleSetAdvertListFilter({ filter: currentFilter }));
    }
  }, [treeData]);

  // Expanded keys değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (expandedKeys.length > 0) {
      localStorage.setItem('treeExpandedKeys', JSON.stringify(expandedKeys));
    }
  }, [expandedKeys]);



  return (
    <div
      ref={treeRef}
      className={`!rounded-lg !max-h-[800px] !overflow-auto !border !border-[#ececec] ${wrapperClasName}`}
    >
      {isLoading && (
        <>
          <GeneralLoadingSpinner />
        </>
      )}
      <Tree
        showLine={true}
        expandedKeys={expandedKeys}
        onExpand={(keys) => {
          setExpandedKeys(keys);
          localStorage.setItem('treeExpandedKeys', JSON.stringify(keys));
        }}
        onSelect={onSelect}
        treeData={treeData}
      />
    </div>
  );
};

export default TreeCategories;